import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
  memo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import {
  fetchEligibilityHistory,
  downloadEligibilityFile,
} from "../../api/eligibilities";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";
import StatusBadge from "./StatusBadge";

const STATUS_OPTIONS = ["pending", "processing", "completed", "failed"];

// Add type for API response
interface EligibilityHistoryApiResponse {
  data: any[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const AutoRefreshIndicator = memo(
  ({
    hasProcessingItems,
    isFetching,
  }: {
    hasProcessingItems: boolean;
    isFetching: boolean;
  }) => {
    const [secondsLeft, setSecondsLeft] = useState(5);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Reset countdown when data finishes fetching
    useEffect(() => {
      if (!isFetching && hasProcessingItems) {
        setSecondsLeft(5);
      }
    }, [isFetching, hasProcessingItems]);

    useEffect(() => {
      // Only run countdown timer if there are processing items
      if (hasProcessingItems) {
        intervalRef.current = setInterval(() => {
          setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 5));
        }, 1000);
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [hasProcessingItems]);

    if (!hasProcessingItems) return null;

    return (
      <span className="text-xs text-white ml-auto mr-1 italic">
        Automatically updates in {secondsLeft} second
        {secondsLeft === 1 ? "" : "s"}
      </span>
    );
  }
);

export default function EligibilityHistoryTable() {
  const { getAccessTokenSilently } = useAuth0();
  const [statusFilter, setStatusFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const handleDownload = useCallback(
    async (id: string, fileName: string) => {
      try {
        const token = await getAccessTokenSilently();
        const response = await downloadEligibilityFile(token, id);

        // Create a temporary link to download the file
        const link = document.createElement("a");
        link.href = response.downloadUrl;
        link.download = fileName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Download failed:", error);
        alert("Failed to download file. Please try again.");
      }
    },
    [getAccessTokenSilently]
  );

  const { data, isLoading, error, isFetching } =
    useQuery<EligibilityHistoryApiResponse>({
      queryKey: [
        "eligibility-history",
        statusFilter,
        pagination.pageIndex,
        pagination.pageSize,
        sorting,
      ],
      queryFn: async () => {
        const token = await getAccessTokenSilently();
        const sort = sorting[0] || { id: "createdAt", desc: true };
        return fetchEligibilityHistory(token, {
          status: statusFilter || undefined,
          page: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          sortBy: sort.id,
          sortDir: sort.desc ? "desc" : "asc",
        });
      },
      refetchInterval: 5000,
    });

  // Check if there are processing items
  const hasProcessingItems = useMemo(() => {
    return (
      data?.data?.some((row: any) =>
        ["processing", "pending"].includes(row.status.toLowerCase())
      ) ?? false
    );
  }, [data?.data]);

  // Efficiently memoize table data by comparing actual content changes
  const prevDataRef = useRef<string>("");
  const prevTableDataRef = useRef<any[]>([]);

  const tableData = useMemo(() => {
    if (!data?.data) return [];

    // Create a stable key from the essential data
    const dataKey = data.data
      .map(
        (row) =>
          `${row._id}-${row.fileName}-${row.status}-${row.processedRows}-${row.createdAt}`
      )
      .join("|");

    // Only re-compute if the data content has actually changed
    if (dataKey === prevDataRef.current) {
      return prevTableDataRef.current; // Return previous computed value
    }

    prevDataRef.current = dataKey;

    prevTableDataRef.current = data?.data;
    return data?.data;
  }, [data?.data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "fileName",
        header: "File",
        cell: (info: any) => {
          const fileName = info.getValue();
          const rowId = info.row.original._id;
          return (
            <button
              onClick={() => handleDownload(rowId, fileName)}
              className="text-blue-600 underline hover:text-blue-800 text-left"
              title="Click to download file"
            >
              {fileName}
            </button>
          );
        },
      },
      {
        accessorKey: "formattedCreatedAt",
        header: "Date",
        cell: (info: any) => info.getValue(),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info: any) => <StatusBadge status={info.getValue()} />,
      },
      {
        accessorKey: "processedRows",
        header: "# Processed",
        cell: (info: any) => info.getValue(),
      },
      {
        accessorKey: "viewUrl",
        header: "View",
        cell: (info: any) => {
          return (
            <Link
              to={`/admin/eligibilities/${info.row.original._id}`}
              className="text-blue-600 underline hover:text-blue-800"
            >
              View
            </Link>
          );
        },
        enableSorting: false,
      },
    ],
    [handleDownload]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    pageCount: data?.totalPages ?? -1,
    manualSorting: true,
    manualFiltering: false,
    debugTable: false,
  });

  return (
    <div className="space-y-4">
      {/* Auto-refresh indicator - separate from table to prevent re-renders */}
      <AutoRefreshIndicator
        hasProcessingItems={hasProcessingItems}
        isFetching={isFetching}
      />

      <div className="p-4 bg-white rounded shadow">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              className="border rounded px-2 py-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          {/* Show subtle loading indicator during refetch */}
          {isFetching && data && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <svg
                className="animate-spin h-3 w-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
              Updating...
            </span>
          )}
        </div>
        {isLoading && !data ? (
          <div className="flex items-center justify-center py-8">
            <svg
              className="animate-spin h-8 w-8"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
          </div>
        ) : error ? (
          <div className="text-red-600 py-8">Error loading history.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200">
                <thead className="bg-gray-100">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-4 py-2 text-left text-xs font-semibold text-gray-700 border-b cursor-pointer select-none"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getIsSorted() === "asc" && " ▲"}
                          {header.column.getIsSorted() === "desc" && " ▼"}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center py-8 text-gray-500"
                      >
                        No history found.
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-4 py-2 border-b">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-4">
              <div>
                <button
                  className="px-3 py-1 border rounded mr-2 disabled:opacity-50"
                  onClick={() =>
                    table.setPageIndex(
                      table.getState().pagination.pageIndex - 1
                    )
                  }
                  disabled={table.getState().pagination.pageIndex === 0}
                >
                  Prev
                </button>
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  onClick={() =>
                    table.setPageIndex(
                      table.getState().pagination.pageIndex + 1
                    )
                  }
                  disabled={
                    data?.totalPages
                      ? table.getState().pagination.pageIndex + 1 >=
                        data.totalPages
                      : false
                  }
                >
                  Next
                </button>
              </div>
              <div className="text-sm">
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {data?.totalPages ?? 1}
              </div>
              <div>
                <select
                  className="border rounded px-2 py-1"
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 30, 40, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
